import { defineStore } from 'pinia';
import {
    BlastPacketForOffsetViewModel,
    BlastPacketLocationForOffsetViewModel,
    BlastPacketRingForOffsetViewModel,
    BlastPacketRingTargetCompletionDto,
    BlastPacketTargetForOffsetViewModel,
    ProductionFrontRulesViewModel,
    QuantityMetric,
    RingRuleEvaluationState,
    RingStatesForWindowViewModel,
    RingStatesLocationForWindowViewModel,
} from '@/models/api';
import { useSystemStore } from '@/lib/stores/SystemStore';
import BlastPackets from '@/lib/data/BlastPackets';
import { AffectedAreaSpecification, useHeadingsStore } from '@/lib/stores/HeadingsStore';
import { useShiftDetails } from '@/lib/stores/Shift';
import { TimespanContainsTask } from '@/lib/services/Task';
import _ from 'lodash';
import dayjs from 'dayjs';
import { useDepartmentStore } from '@/lib/stores/DepartmentStore';
import { BlastPacketRingTargetEvaluation, LocationRingStateMachine } from '@/lib/services/Production';
import {
    ClientBlastPacketActualsEntry,
    ClientBlastPacketTargetCompletion,
    ClientBlastPacketTargetCompletionUndo
} from '@/models/client/client-actuals';
import { transformPlodsToClient } from '@/lib/stores/Transforms';
import UserStore from '@/lib/stores/UserStore';
import { useWindowedActuals } from '@/lib/stores/Production/WindowedActualsStore';
import { RowType } from '@/models/client/types/row-type';
import { ProductionValidationTaskModel } from '@/models/client/production-validation-task-model';
import { ProductionValidationRow } from '@/models/client/production-validation-row';
import selectedMineAreaStore from '@/lib/stores/SelectedMineAreaStore';
import { validateProductionFrontRules } from '@/components/ShiftBoard/Board/Validation';
import { ClientTaskWarning } from '@/models/client/client-task-warning';
import { DefaultTaskBackgroundColour, DefaultTaskForegroundColour } from '@/lib/services/TaskType';
import Actuals from '@/lib/data/Actuals';

export interface BlastPacketActualsSuggestion {
    id?: string | null,
    targetId: string,
    locationName: string,
    locationId: string,
    ringName: string,
    targetName: string,
    targetOrder: number,
    quantity?: number,
    startTime?: dayjs.Dayjs,
    endTime: dayjs.Dayjs,
    comment?: string | null,
    completed: boolean,
    completionId?: string | null,
    dirty: boolean,
    delete: boolean
}

export interface ClientRowBlastPacketInformation {
    blastPacketName: string,
    blastPacketId: string,
    locationId: string,
    fireTarget: ClientRowBlastPacketTargetInformation,
    bogTarget: ClientRowBlastPacketTargetInformation,
    rings: ClientRowBlastPacketRingInformation[],
}

export interface ClientRowBlastPacketRingInformation {
    ringName: string,
    ringId: string,
    locationId: string,
    targets: ClientRowBlastPacketTargetInformation[],
}

export interface ClientRowBlastPacketTargetInformation {
    targetId: string,
    taskName: string,
    taskBackgroundColour: string | null,
    taskForegroundColour: string | null,
    targetQuantity: number,
    taskTypeId: string,
    order: number,
    defaultRate: number | null,
    isCompletedOrReachedTarget: boolean,
    tolerance: number,
}

export interface ClientRowBlastPacketTargetDisplayInformation {
    targetId: string,
    blastPacketName: string,
    blastPacketId: string,
    ringName: string | null,
    ringId: string | null,
}

function convertServerBlastPacketTargetToClientTarget(t: BlastPacketTargetForOffsetViewModel): ClientRowBlastPacketTargetInformation {
    return {
        targetId: t.id ?? '',
        taskName: t.taskName ?? 'Unknown',
        taskBackgroundColour: t.taskBackgroundColour,
        taskForegroundColour: t.taskForegroundColour,
        targetQuantity: t.target,
        taskTypeId: t.taskTypeId ?? '',
        order: t.taskOrder,
        defaultRate: t.ratePerHour ?? null,
        isCompletedOrReachedTarget: t.markedCompleted || t.actualsPercentage >= 100,
        tolerance: t.tolerance,
    };
}

export const useShiftWindowActuals = defineStore('shiftWindowActuals', {
    state: () => {
        return {
            locationBlastPacketInformation: [] as BlastPacketLocationForOffsetViewModel[]
        };
    },
    getters: {
        locationBlastPacketRingStateInformation(): RingStatesLocationForWindowViewModel[] {
            return useWindowedActuals().locationBlastPacketRingStateInformation;
        },
        getBlastPacketsInformation(state): ClientRowBlastPacketInformation[] {
            return _.flatMap(
                state.locationBlastPacketInformation,
                l=>_.flatMap(
                    l.blastPackets,
                    bp=> ({
                        blastPacketId: bp.id,
                        blastPacketName: bp.name ?? '',
                        bogTarget: convertServerBlastPacketTargetToClientTarget(bp.bogTarget!),
                        fireTarget: convertServerBlastPacketTargetToClientTarget(bp.fireTarget!),
                        locationId: l.id,
                        rings: _.flatMap(
                            bp.rings,
                            r=> ({
                                ringId: r.locationRingId ?? '',
                                ringName: r.locationRingName ?? 'unknown',
                                locationId: r.locationId!,
                                targets: r.targets.map(t=>convertServerBlastPacketTargetToClientTarget(t))
                            })
                        )
                    })
                )
            );
        },
        getBlastPacketRingsInformation(state): ClientRowBlastPacketRingInformation[] {
            return _.flatMap(
                state.locationBlastPacketInformation,
                l=>_.flatMap(
                    l.blastPackets,
                    bp=>_.flatMap(
                        bp.rings,
                        r=> ({
                            ringId: r.locationRingId ?? '',
                            ringName: r.locationRingName ?? 'unknown',
                            locationId: l.id,
                            targets: r.targets.map(t=>({
                                targetId: t.id ?? '',
                                taskName: t.taskName ?? 'Unknown',
                                taskBackgroundColour: t.taskBackgroundColour,
                                taskForegroundColour: t.taskForegroundColour,
                                targetQuantity: t.target,
                                taskTypeId: t.taskTypeId ?? '',
                                order: t.taskOrder,
                                defaultRate: t.ratePerHour ?? null,
                                isCompletedOrReachedTarget: t.markedCompleted || t.actualsPercentage >= 100,
                                tolerance: t.tolerance,
                            }))
                        })
                    )
                )
            );
        },
        getBlastPacketTargetsDisplayInformation(state): ClientRowBlastPacketTargetDisplayInformation[] {
            return _.flatMap(
                state.locationBlastPacketInformation,
                l=>_.flatMap(
                    l.blastPackets,
                    bp=>([
                        {
                            targetId: bp.fireTarget!.id!,
                            blastPacketName: bp.name ?? '',
                            blastPacketId: bp.id,
                            ringName: null,
                            ringId: null
                        },
                        {
                            targetId: bp.bogTarget!.id!,
                            blastPacketName: bp.name ?? '',
                            blastPacketId: bp.id,
                            ringName: null,
                            ringId: null,
                        },
                        ..._.flatMap(
                            bp.rings,
                            r=> _.flatMap(
                                r.targets,
                                t=>({
                                    targetId: t.id!,
                                    blastPacketName: bp.name ?? '',
                                    blastPacketId: bp.id,
                                    ringName: r.locationRingName ?? '',
                                    ringId: r.locationRingId
                                })
                            ))
                        ])
                    )
                );
        },
        getBlastPacketSuggestionsForCurrentShift(state): BlastPacketActualsSuggestion[]  {
            const targetsWithActualsInThisShift = this.getActualsInCurrentShift.map(e=>e.targetId);
            const shiftDetailsStore = useShiftDetails();
            const blastPacketTasksInShift = useHeadingsStore().tasks.filter(t=> !!t.blastPacketRingTargetId && TimespanContainsTask(t,shiftDetailsStore.shiftStartTime, shiftDetailsStore.shiftEndTime));
            const blastPacketTargetsInShift = _.uniq(blastPacketTasksInShift.map(t=>t.blastPacketRingTargetId!));

            return this.getBlastPacketSuggestions.filter(t=>blastPacketTargetsInShift.includes(t.targetId) && !targetsWithActualsInThisShift.includes(t.targetId));
        },
        getActualsInCurrentShift(state): ClientBlastPacketActualsEntry[] {
            const shiftDetailsStore = useShiftDetails();
            return useWindowedActuals().localActualPhysicalEntries.filter(entry=>entry.endTime.isAfter(shiftDetailsStore.shiftStartTime) && entry.endTime.isSameOrBefore(shiftDetailsStore.shiftEndTime));
        },
        getBlastPacketSuggestions(state): BlastPacketActualsSuggestion[] {
            const shiftEndDate = useShiftDetails().shiftEndTime;
            return _.flatMap(
            state.locationBlastPacketInformation,
                l=>_.flatMap(
                        l.blastPackets,
                        bp=>_.flatMap(
                            bp.rings,
                            r=> _.flatMap(r.targets, t=>({
                                targetId: t.id!,
                                locationName: l.name!,
                                locationId: l.id,
                                ringName: r.locationRingName!,
                                targetName: t.taskName!,
                                targetOrder: t.taskOrder!,
                                endTime: shiftEndDate,
                                completed: t.markedCompleted,
                                completionId: t.completionId,
                                dirty: false,
                                delete: false,
                            }))
                        )
                )
        )},
        getBlastPacketRingTargetToRingMap(state): Map<string,string> {
            return new Map<string,string>(_.flatMap(this.getBlastPacketRingsInformation, r=>r.targets.map(t=>[t.targetId!, r.ringId!])));
        },
        getRingStateAtTimePoint(state): (locationId: string, locationRingId: string, timePoint: dayjs.Dayjs) => RingRuleEvaluationState | null {
            return (locationId: string, locationRingId: string, timePoint: dayjs.Dayjs) => {
                const locationStateInformation = useWindowedActuals().locationBlastPacketRingStateInformation.find(si=>si.locationId === locationId);

                if(locationStateInformation == null)
                    return null;

                const states = locationStateInformation.rings.find(r=>r.locationRingId === locationRingId)?.states;

                if(states == null)
                    return null;

                return _.last(states.filter(s=>dayjs(s.startAt).utc().isBefore(timePoint) && dayjs(s.endAt).utc().isSameOrAfter(timePoint))) ?? null;
            }
        },
        getRingStatesAtTimePoint(state): (stopeId: string, blastPacketId: string, timePoint: dayjs.Dayjs) => RingRuleEvaluationState[] {
            return (stopeId: string, blastPacketId: string, timePoint: dayjs.Dayjs): RingRuleEvaluationState[] => {
                const locationStateInformation = useWindowedActuals().locationBlastPacketRingStateInformation.filter((si:RingStatesLocationForWindowViewModel)=>si.stopeId === stopeId);

                if(locationStateInformation.length == 0)
                    return [];

                const ringsStateInformation = _.flatMap(locationStateInformation, (si)=>si.rings);

                return ringsStateInformation.filter((r:RingStatesForWindowViewModel)=>r.blastPacketId === blastPacketId)
                    .map((r:RingStatesForWindowViewModel)=>_.last(r.states.filter(s=>dayjs(s.startAt).utc().isBefore(timePoint) && dayjs(s.endAt).utc().isSameOrAfter(timePoint))) ?? null)
                    .filter((s:RingRuleEvaluationState | null)=>s != null)
                    .map((s:RingRuleEvaluationState | null)=>s!);
            }
        },
        getRingStatesUpToTimePoint(state): (stopeId: string, blastPacketId: string, timePoint: dayjs.Dayjs) => RingRuleEvaluationState[] {
            return (stopeId: string, blastPacketId: string, timePoint: dayjs.Dayjs): RingRuleEvaluationState[] => {
                const locationStateInformation = useWindowedActuals().locationBlastPacketRingStateInformation.filter((si:RingStatesLocationForWindowViewModel)=>si.stopeId === stopeId);

                if(locationStateInformation.length == 0)
                    return [];

                const ringsStateInformation = _.flatMap(locationStateInformation, (si)=>si.rings);

                return ringsStateInformation.filter((r:RingStatesForWindowViewModel)=>r.blastPacketId === blastPacketId)
                    .map((r:RingStatesForWindowViewModel)=>r.states.filter(s=>dayjs(s.startAt).utc().isBefore(timePoint)))
                    .flatMap((s:RingRuleEvaluationState[])=>s);
            }
        }
    },
    actions: {
        async populateWindow(mineId: string, departmentId: string, date: string, shiftNumber: string, offsetPrior: number, offsetFollowing: number) {
            if(useSystemStore().isOnline){
                const blastPacketsResponse = await BlastPackets.getWindowByShift(mineId, departmentId, date, shiftNumber, offsetPrior, offsetFollowing);
                const ringsStateResponse = await BlastPackets.getStatesWindowByShift(mineId, departmentId, date, shiftNumber, offsetPrior, offsetFollowing);
                const actualsResponse = await Actuals.getWindowByShift(mineId, departmentId, date, shiftNumber, offsetPrior, offsetFollowing);
                const completions = _.flatMap(
                    blastPacketsResponse.locations,
                    l=>_.flatMap(
                        l.blastPackets,
                        bp=>_.flatMap(
                            bp.rings,
                            r=> _.flatMap(r.targets.filter(t=>t.completionId != null), t=>({
                                id: t.completionId!,
                                targetId: t.id!,
                                completionReason: t.completionReason,
                                completedAt: dayjs(t.completedAt!).utc(),
                                locationId: r.locationId!
                            }))
                        )
                    ));

                const actuals = transformPlodsToClient(actualsResponse.entries);

                this.setUpStore(blastPacketsResponse.locations);

                useWindowedActuals().setUpStore(dayjs(ringsStateResponse.clientCalculationTime).utc(), this.getBlastPacketsInformation, ringsStateResponse.locations, actuals, completions)
            }
        },
        setUpStore(locationBlastPacketInformation: BlastPacketLocationForOffsetViewModel[]) {
            this.locationBlastPacketInformation = locationBlastPacketInformation;
        },
        reEvaluateRingStatesForHeadings(headings: { rowType: RowType, stopeInfo: { stopeId: string | null, supportsRings: boolean } | null, location: { id: string, name: string | null } | null, tasks: ProductionValidationTaskModel[] }[], ringErrorAccumulator: (t: ProductionValidationTaskModel, affectedArea: AffectedAreaSpecification, warning: ClientTaskWarning)=>void) {
            useWindowedActuals().reEvaluateRingStatesForHeading(headings, ringErrorAccumulator);
        },
        validateProductionFrontRules(rows: ProductionValidationRow[], rulesets: ProductionFrontRulesViewModel[], warningAdditionFunc: (task: ProductionValidationTaskModel, affectedArea: AffectedAreaSpecification, warning: ClientTaskWarning) => void) {
            const shiftStartTime = useShiftDetails().shiftStartTime;
            const shiftEndTime = useShiftDetails().shiftEndTime;

            const ringStates = this.locationBlastPacketRingStateInformation;

            const currentMineTime = selectedMineAreaStore.getMineCurrentTime();
            const currentMineTimeComparableToTasks = currentMineTime.utc(true);

            validateProductionFrontRules(rows, this.getBlastPacketTargetsDisplayInformation, rulesets, ringStates, currentMineTimeComparableToTasks, [{ shiftStartTime, shiftEndTime }], warningAdditionFunc);
        }
    }
});